import { AsyncPipe, DecimalPipe } from '@angular/common';
import {
  Component,
  inject,
  OnDestroy,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TuiDay, TuiValidationError } from '@taiga-ui/cdk';
import {
  TuiAppearance,
  TuiButton,
  TuiDataList,
  tuiDateFormatProvider,
  TuiDialogContext,
  TuiError,
  TuiGroup,
  TuiLabel,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  TuiAvatar,
  TuiBlock,
  TuiDataListWrapper,
  TuiFieldErrorPipe,
  TuiFileLike,
  TuiFiles,
  TuiFilterByInputPipe,
  tuiItemsHandlersProvider,
  TuiRadio,
  TuiSwitch,
} from '@taiga-ui/kit';
import { TuiCardLarge } from '@taiga-ui/layout';
import {
  TuiComboBoxModule,
  TuiInputDateModule,
  TuiInputModule,
  TuiTextareaModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/legacy';
import { injectContext } from '@taiga-ui/polymorpheus';
import { finalize, map, Observable, of, Subject, switchMap, timer } from 'rxjs';
import { Organisme } from '../../models/organisme';
import { Patient } from '../../models/patient';
import { PrescriptionFormDialogData } from '../../models/prescription-form-dialog-data';
import { PpcDapInformationsForm } from '../../models/prescriptionForm/ppcDapInformationsForm';
import { PpcInformationsComplementaires } from '../../models/prescriptionForm/ppcInformationsComplementaires';
import { PpcOrdoclicInformationsForm } from '../../models/prescriptionForm/ppcOrdoclicInformationsForm';
import { PpcOrdonnanceInformationsForm } from '../../models/prescriptionForm/ppcOrdonnanceInformationsForm';
import { PrescriptionForm } from '../../models/prescriptionForm/prescriptionForm';
import { User } from '../../models/user';
import { AuthService } from '../../services/auth.service';
import { CommonService } from '../../services/common.service';
import { AuthOrdoclicService } from '../../services/ordoclic/auth.service';
import { OrdoclicLogsService } from '../../services/ordoclic/ordoclic-logs.service';
import { OrdoclicService } from '../../services/ordoclic/ordoclic.service';
import { PrescriptionFormService } from '../../services/prescription-form.service';
import { Prescription } from '../../models/prescription';

@Component({
  selector: 'app-prescription-form-ppc',
  standalone: true,
  imports: [
    TuiCardLarge,
    TuiAppearance,
    ReactiveFormsModule,
    TuiLabel,
    TuiError,
    TuiFieldErrorPipe,
    AsyncPipe,
    TuiInputModule,
    TuiTextfield,
    TuiTextfieldControllerModule,
    TuiBlock,
    TuiGroup,
    TuiRadio,
    TuiSwitch,
    TuiFiles,
    TuiInputDateModule,
    TuiTextareaModule,
    TuiComboBoxModule,
    TuiFilterByInputPipe,
    TuiDataList,
    TuiDataListWrapper,
    TuiButton,
    TuiAvatar,
  ],
  templateUrl: './prescription-form-ppc.component.html',
  styleUrl: './prescription-form-ppc.component.scss',
  providers: [
    tuiDateFormatProvider({ mode: 'DMY', separator: '/' }),
    tuiItemsHandlersProvider({
      stringify: (item: User | string) =>
        item instanceof User ? `${item.nom} ${item.prenom}` : item,
    }),
  ],
})
export class PrescriptionFormPpcComponent implements OnDestroy {
  public readonly context =
    injectContext<TuiDialogContext<void, PrescriptionFormDialogData | void>>();

  protected authService = inject(AuthService);
  protected commonService = inject(CommonService);
  protected authOrdoclicService = inject(AuthOrdoclicService);
  protected ordoclicService = inject(OrdoclicService);
  protected ordoclicLogsService = inject(OrdoclicLogsService);
  private prescriptionFormService = inject(PrescriptionFormService);

  public etatPush: undefined | boolean | 'loading';
  public connexionOrdoclic: boolean = false;

  public typeSlug!: string;

  private iframeWindow: Window | null = null;

  public form!: FormGroup;

  public patient!: Patient | undefined;
  public prescription!: Prescription | undefined;
  public codeDActivite!: string;
  public userList!: User[];
  public organisme!: Organisme | null;
  public reedition!: boolean;
  public prescriptionFormPrecedente!: PrescriptionForm | undefined;
  public prescriptionFormPrecedenteInformationsComplementaires!:
    | PpcInformationsComplementaires
    | undefined;

  protected readonly failedFiles$ = new Subject<TuiFileLike | null>();
  protected readonly loadingFiles$ = new Subject<TuiFileLike | null>();
  protected loadedFiles$?: Observable<TuiFileLike | null>;

  protected initiale: WritableSignal<boolean> = signal(false);

  ngOnInit(): void {
    this.patient = this.context.data!.patient;
    this.prescription = this.context.data!.prescription;

    this.codeDActivite = this.context.data!.codeDActivite;
    this.userList = this.context.data!.userList;
    this.organisme = this.context.data!.organisme;
    this.reedition = this.context.data!.reedition;
    this.prescriptionFormPrecedente =
      this.context.data!.prescriptionFormPrecedente;
    this.prescriptionFormPrecedenteInformationsComplementaires =
      this.prescriptionFormPrecedente?.informationsComplementaires instanceof
      PpcInformationsComplementaires
        ? this.prescriptionFormPrecedente.informationsComplementaires
        : undefined;

    let user = this.authService.getUser();

    this.initiale.set(this.prescription?.type != 'prolongation');

    this.typeSlug = (() => {
      if (this.patient?.acceptOrdonnance() && !this.initiale()) {
        return 'ppc_ordonnance';
      } else {
        if (user?.role?.slug == 'liberal') {
          return 'ppc_ordoclic';
        } else {
          return 'ppc_dap';
        }
      }
    })();

    this.form = new FormGroup({
      typeSlug: new FormControl(this.typeSlug, [
        Validators.required,
        this.patient && !this.initiale()
          ? Validators.pattern('^(ppc_dap|ppc_ordoclic|ppc_ordonnance)$')
          : Validators.pattern('^(ppc_dap|ppc_ordoclic)$'),
      ]),
      medecin: new FormControl(
        (() => {
          if (
            this.userList.find(
              (test) => test.id == this.authService.getUser()?.id,
            )
          ) {
            return user;
          } else {
            return null;
          }
        })(),
        this.typeSlug == 'ppc_ordoclic' ? [] : [Validators.required],
      ), // different
      userId: new FormControl(user?.id, [
        Validators.required,
        Validators.pattern('^[0-9]+$'),
      ]),
      nir: new FormControl(this.patient?.nir ?? null, [
        Validators.required,
        Validators.pattern(
          '^[12][0-9]{2}(0[1-9]|1[0-2])(2[ABab]|[0-9]{2})[0-9]{8}$',
        ),
      ]),
      codeDActivite: new FormControl(this.codeDActivite, [Validators.required]),
      dateDePrescription: new FormControl(
        TuiDay.fromLocalNativeDate(this.prescription?.dateDebut ?? new Date()),
        [Validators.required],
      ),
      nom: new FormControl(this.patient?.nom ?? null, [Validators.required]),
      prenom: new FormControl(this.patient?.prenom ?? null, [
        Validators.required,
      ]),
      sexe: new FormControl(
        this.patient?.sexe == 'Homme'
          ? 'homme'
          : this.patient?.sexe == 'Femme'
            ? 'femme'
            : null,
        [Validators.required, Validators.pattern('^(homme|femme)$')],
      ),
      dateDeNaissance: new FormControl(
        this.patient?.dateDeNaissance
          ? TuiDay.fromLocalNativeDate(this.patient.dateDeNaissance)
          : null,
        [Validators.required],
      ),
      adresseLigne1: new FormControl(this.patient?.adresse?.ligne1 ?? null, []),
      adresseLigne2: new FormControl(
        this.patient?.adresse?.ligne2 && this.patient?.adresse?.ligne3
          ? this.patient?.adresse?.ligne2 + ', ' + this.patient?.adresse?.ligne3
          : (this.patient?.adresse?.ligne2 ??
              this.patient?.adresse?.ligne3 ??
              null),
      ),
      adresseCodePostal: new FormControl(
        this.patient?.adresse?.codePostal ?? null,
        [],
      ),
      adresseVille: new FormControl(this.patient?.adresse?.ville ?? null, []),
      telephone: new FormControl(
        this.patient?.mobile ?? this.patient?.telephone ?? null,
        [Validators.required],
      ),
      aSigner: new FormControl(
        (() => {
          if (
            this.patient?.canSign ||
            (!this.patient && this.authService.getUser()?.rpps)
          ) {
            return true;
          } else {
            return false;
          }
        })(),
      ),

      // Informations complémentaires PPC
      ppcTypeSlug: new FormControl(
        !this.initiale() && this.patient
          ? this.patient.dernierePrescription?.type != 'initiale' ||
            this.prescriptionFormPrecedenteInformationsComplementaires
              ?.ppcTypeSlug != 'dap_initial'
            ? 'dap_annuel'
            : 'dap_4mois'
          : 'dap_initial',
        [
          Validators.required,
          this.initiale()
            ? Validators.pattern('^(dap_initial)$')
            : Validators.pattern('^(dap_4mois|dap_annuel)$'),
        ],
      ),
      examenRealise: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires
          ?.examen_realise ?? (this.initiale() ? null : 'polygraphie'),
        [
          Validators.required,
          Validators.pattern('^(polysomnographie|polygraphie)$'),
        ],
      ),
      microEveils: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires
          ?.microEveils ??
          (this.prescriptionFormPrecedenteInformationsComplementaires
            ?.examen_realise == 'polysomnographie'
            ? 0
            : null),
      ),
      iahGap: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires?.iahGap ??
          (this.initiale() ? null : 'gt15_lt30'),
        [Validators.required, Validators.pattern('^(lt15|gt15_lt30|gt30)$')],
      ),
      compteRendu: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires
          ?.compteRendu ?? null,
      ),
      somnolenceDiurne: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires
          ?.somnolenceDiurne ?? false,
      ),
      ronflementsSeveres: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires
          ?.ronflementsSeveres ?? false,
      ),
      cephaleesMatinales: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires
          ?.cephaleesMatinales ?? false,
      ),
      fatigueDiurne: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires
          ?.fatigueDiurne ?? false,
      ),
      nycturie: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires?.nycturie ??
          false,
      ),
      sensationEtouffement: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires
          ?.sensationEtouffement ?? false,
      ),
      somnolenceDiurneSevereRisqueAccidentel: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires
          ?.somnolenceDiurneSevereRisqueAccidentel ?? false,
      ),
      comorbiditeCardioVasculaireGrave: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires
          ?.comorbiditeCardioVasculaireGrave ?? false,
      ),
      comorbiditeRespiratoireGrave: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires
          ?.comorbiditeRespiratoireGrave ?? false,
      ),
      nombreSymptomesCoches: new FormControl(
        (this.prescriptionFormPrecedenteInformationsComplementaires
          ?.somnolenceDiurne
          ? 1
          : 0) +
          (this.prescriptionFormPrecedenteInformationsComplementaires
            ?.ronflementsSeveres
            ? 1
            : 0) +
          (this.prescriptionFormPrecedenteInformationsComplementaires
            ?.cephaleesMatinales
            ? 1
            : 0) +
          (this.prescriptionFormPrecedenteInformationsComplementaires
            ?.fatigueDiurne
            ? 1
            : 0) +
          (this.prescriptionFormPrecedenteInformationsComplementaires?.nycturie
            ? 1
            : 0) +
          (this.prescriptionFormPrecedenteInformationsComplementaires
            ?.sensationEtouffement
            ? 1
            : 0),
        this.initiale()
          ? [Validators.required, this.strictGreaterThanZeroValidator()]
          : [],
      ),
      nombrePathologiesCoches: new FormControl(
        (this.prescriptionFormPrecedenteInformationsComplementaires
          ?.somnolenceDiurneSevereRisqueAccidentel
          ? 1
          : 0) +
          (this.prescriptionFormPrecedenteInformationsComplementaires
            ?.comorbiditeCardioVasculaireGrave
            ? 1
            : 0) +
          (this.prescriptionFormPrecedenteInformationsComplementaires
            ?.comorbiditeRespiratoireGrave
            ? 1
            : 0),
      ),
      oam12DerniersMoisNon: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires
          ?.oam12DerniersMoisNon ?? false,
      ),
      reglage: new FormControl(
        this.prescriptionFormPrecedenteInformationsComplementaires?.reglage ??
          null,
      ),

      // Informations Formulaire PPC DAP
      caisseNom: new FormControl(
        this.organisme?.nom ?? (this.initiale() ? null : 'Ø'),
        [],
      ),
      caisseNumero: new FormControl(
        this.organisme?.numero ?? (this.initiale() ? null : 'Ø'),
        [],
      ),
      assureNomComplet: new FormControl(null),
      assureNir: new FormControl(null),
      assureAdresse: new FormControl(null),
      maladie: new FormControl(false),
      ald: new FormControl(false),
      maladieDetail: new FormControl(null),
      traitementPrescritType: new FormControl('ppc', [
        Validators.required,
        Validators.pattern('^(ppc|oam)$'),
      ]),
      ppcAppareilType: new FormControl(
        (() => {
          switch (this.patient?.tableauDeBord?.mode?.value) {
            case 'PPC FIXE':
              return 'a_pression_fixe';
              break;
            case 'PPC AUTO':
              return 'autopilotee';
              break;
            case 'BI LEVEL':
            case 'ASV-CS':
            case 'S/T':
            case 'iVAPS':
            case 'VAUTO':
            case 'S':
              return 'a_double_niveau_de_pression';
              break;
            default:
              return this.initiale() ? null : 'a_pression_fixe';
              break;
          }
        })(),
        [
          Validators.pattern(
            '^(a_pression_fixe|a_double_niveau_de_pression|autopilotee)$',
          ),
          Validators.required,
        ],
      ),
      ppcPression: new FormControl(
        this.patient?.tableauDeBord?.p90_95?.value
          ?.toString()
          .replace(/cmH2O/g, '') ?? 'Ø',
        [Validators.required],
      ),
      ppcInterfaceType: new FormControl(
        this.patient?.tableauDeBord?.typeDeMasque?.value ??
          (this.initiale() ? null : 'Ø'),
        [Validators.required],
      ),
      ppcModeleMarque: new FormControl(
        this.patient?.tableauDeBord?.materielEnlocationList &&
          this.patient?.tableauDeBord?.materielEnlocationList[0]
          ? this.patient!.tableauDeBord!.materielEnlocationList![0]!.libelle
          : null,
      ),
      oamExamenRealise: new FormControl(false),
      oamDate: new FormControl(null, [
        Validators.pattern('^\\d{4}-\\d{2}-\\d{2}$'),
      ]),
      oamType: new FormControl(null, [
        Validators.pattern('^(initial|renouvellement)$'),
      ]),
      oamModeleMarqueEtType: new FormControl(null),
      medecinConseilAccord: new FormControl(null),
      medecinConseilConvocation: new FormControl(false),
      medecinConseilRefusType: new FormControl(null, [
        Validators.pattern('^(medical|administratif)$'),
      ]),
      medecinConseilDate: new FormControl(null, [
        Validators.pattern('^\\d{4}-\\d{2}-\\d{2}$'),
      ]),
      initialeComorbiditeCardioVasculaireAssocieeGraveMotif: new FormControl(
        null,
      ), // perte de donnée
      initialeComorbiditeRespiratoireAssocieeGraveMotif: new FormControl(null), // perte de donnée
      renouvellementIah: new FormControl(
        new DecimalPipe('fr').transform(
          this.patient?.iahResiduel?.value,
          '1.0-0',
        ),
      ),
      renouvellementAmeliorationSymptomatologie: new FormControl(true),
      renouvellementRefusReleveObservance: new FormControl(false),
      renouvellementPatientObservant: new FormControl(
        (this.patient?.obs?.value as number) >= 4,
      ),
      oamIah: new FormControl(null),
      oamAmeliorationSymptomatologie: new FormControl(false),
      refusOuIntolerancePpc: new FormControl(false),
      refusOuIntoleranceOam: new FormControl(false),
      commentaire: new FormControl(
        this.initiale()
          ? "Indication d'une OAM évaluée, mais non retenue au regard de la situation clinique de ce patient."
          : this.patient?.obs?.value
            ? 'Observance : ' +
              new DecimalPipe('fr').transform(this.patient.obs.value, '1.2-2')
            : '',
      ),
    });

    this.form.get('somnolenceDiurne')?.valueChanges.subscribe(() => {
      this.updateNombreSymptomesPathologiesCoches();
    });
    this.form.get('ronflementsSeveres')?.valueChanges.subscribe(() => {
      this.updateNombreSymptomesPathologiesCoches();
    });
    this.form.get('cephaleesMatinales')?.valueChanges.subscribe(() => {
      this.updateNombreSymptomesPathologiesCoches();
    });
    this.form.get('fatigueDiurne')?.valueChanges.subscribe(() => {
      this.updateNombreSymptomesPathologiesCoches();
    });
    this.form.get('nycturie')?.valueChanges.subscribe(() => {
      this.updateNombreSymptomesPathologiesCoches();
    });
    this.form.get('sensationEtouffement')?.valueChanges.subscribe(() => {
      this.updateNombreSymptomesPathologiesCoches();
    });
    this.form
      .get('somnolenceDiurneSevereRisqueAccidentel')
      ?.valueChanges.subscribe(() => {
        this.updateNombreSymptomesPathologiesCoches();
      });
    this.form
      .get('comorbiditeCardioVasculaireGrave')
      ?.valueChanges.subscribe(() => {
        this.updateNombreSymptomesPathologiesCoches();
      });
    this.form
      .get('comorbiditeRespiratoireGrave')
      ?.valueChanges.subscribe(() => {
        this.updateNombreSymptomesPathologiesCoches();
      });

    this.form.get('examenRealise')?.valueChanges.subscribe(() => {
      this.updateMicroEveils();
    });

    this.updateMicroEveils();

    this.loadedFiles$ = this.form
      .get('compteRendu')
      ?.valueChanges.pipe(switchMap((file) => this.processFile(file)));

    if (this.typeSlug == 'ppc_ordoclic') {
      this.authenticate(user ?? undefined);
    }
  }

  updateMicroEveils(): void {
    if (this.form.get('examenRealise')?.value == 'polysomnographie') {
      this.form.get('microEveils')?.enable();
      if (this.authService.getUser()?.role?.slug == 'liberal') {
        this.form
          .get('microEveils')
          ?.setValidators([
            Validators.required,
            Validators.pattern('^[0-9]+$'),
          ]);
      }
    } else {
      this.form.get('microEveils')?.disable();
      this.form.get('microEveils')?.clearValidators();
      this.form.get('microEveils')?.reset();
    }
  }
  updateNombreSymptomesPathologiesCoches(): void {
    let nombreSymptomesCoches: number = 0;
    let nombrePathologiesCoches: number = 0;

    if (this.form.get('somnolenceDiurne')?.value) nombreSymptomesCoches++;
    if (this.form.get('ronflementsSeveres')?.value) nombreSymptomesCoches++;
    if (this.form.get('cephaleesMatinales')?.value) nombreSymptomesCoches++;
    if (this.form.get('fatigueDiurne')?.value) nombreSymptomesCoches++;
    if (this.form.get('nycturie')?.value) nombreSymptomesCoches++;
    if (this.form.get('sensationEtouffement')?.value) nombreSymptomesCoches++;
    if (this.form.get('somnolenceDiurneSevereRisqueAccidentel')?.value)
      nombrePathologiesCoches++;
    if (this.form.get('comorbiditeCardioVasculaireGrave')?.value)
      nombrePathologiesCoches++;
    if (this.form.get('comorbiditeRespiratoireGrave')?.value)
      nombrePathologiesCoches++;

    this.form.get('nombreSymptomesCoches')?.setValue(nombreSymptomesCoches);
    this.form.get('nombrePathologiesCoches')?.setValue(nombrePathologiesCoches);
  }

  protected processFile(
    file: TuiFileLike | null,
  ): Observable<TuiFileLike | null> {
    this.failedFiles$.next(null);

    if (this.form.get('compteRendu')?.invalid || !file) {
      return of(null);
    }

    this.loadingFiles$.next(file);

    return timer(1000).pipe(
      map(() => {
        const isPdf = file.type === 'application/pdf';
        const isSizeOk = typeof file.size === 'number' && file.size <= 5500000;

        if (!isPdf || !isSizeOk) {
          this.failedFiles$.next(file);

          // Pousser les erreurs dans le FormControl pour <tui-error>
          const control = this.form.get('compteRendu');
          const errors: any = {
            ...(control?.errors ?? {}),
            ...(isPdf
              ? {}
              : {
                  pdfOnly: new TuiValidationError(
                    'Seuls les fichiers PDF sont acceptés.',
                  ),
                }),
            ...(isSizeOk
              ? {}
              : { maxSize: new TuiValidationError(`Le fichier dépasse 5Mo.`) }),
          };
          control?.setErrors(errors);

          return null;
        } else {
          this.form.get('compteRendu')?.setErrors(null);
          return file;
        }
      }),
      finalize(() => this.loadingFiles$.next(null)),
    );
  }

  protected removeFile = (): void => {
    this.form.patchValue({
      compteRendu: null,
    });
  };

  selectMe() {
    this.form.get('medecin')?.setValue(this.authService.getUser());
  }

  protected readonly matcherString = (user: User, search: string): boolean =>
    (`${user.prenom}`
      .split(' ')
      .pop()
      ?.toLowerCase()
      .startsWith(search.toLowerCase()) ||
      `${user.nom}`
        .split(' ')
        .pop()
        ?.toLowerCase()
        .startsWith(search.toLowerCase())) ??
    false;

  initEtatPush(): void {
    this.etatPush = undefined;
  }

  async launchOrdoclic(data: any): Promise<any> {
    return this.ordoclicService
      .getOrdoclicAmeliproWindow()
      .then(async (response) => {
        this.iframeWindow = response;
        const mess = await this.ordoclicService.sendMessageToIframe(
          this.iframeWindow,
          data,
        );

        this.ordoclicLogsService.add(JSON.stringify(mess));
        this.ordoclicLogsService.add(data);

        this.ordoclicService.hideModal();

        this.ordoclicLogsService
          .push(this.patient?.id ?? undefined)
          .subscribe();

        let retour: any = {
          recapitulatif: mess.outputs?.recapitulatif?.recapitulatif?.content,
          prescription: mess.outputs?.prescription?.prescription?.content,
        };

        return retour;
      })
      .catch((error) => {
        console.error('Erreur launch Ordoclic : ' + error.getMessage());
        this.etatPush = false;
      });
  }

  closeDialog() {
    this.ordoclicService.hideModal();
  }

  authenticate(user?: User): void {
    if (user && user.rpps) {
      this.authOrdoclicService.generateSignature(user.rpps).subscribe({
        next: (response) => {
          this.connexionOrdoclic = true;
        },
        error: (error) => {
          this.connexionOrdoclic = false;
        },
      });
    } else {
      this.connexionOrdoclic = false;
    }
  }

  strictGreaterThanZeroValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null; // La validation "required" gère déjà ce cas
      }
      return value > 0 ? null : { strictGreaterThanZero: true };
    };
  }

  async post() {
    let user = this.authService.getUser();

    if (this.form.value.ppcTypeSlug == 'dap_4mois')
      this.typeSlug =
        user?.role?.slug == 'liberal' ? 'ppc_ordoclic' : 'ppc_dap';

    if (this.patient) {
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        if (
          control?.errors?.['required'] &&
          (control.value === null || control.value === '')
        ) {
          control.setValue('Ø');
          control.updateValueAndValidity();
        }
      });
    }

    this.form.markAllAsTouched();

    var compteRenduBase64;
    await this.commonService
      .convertFileToBase64(this.form.get('compteRendu')?.value)
      .then((base64File) => {
        compteRenduBase64 = base64File;
      })
      .catch((err) => {
        return;
      });
    if (this.form.invalid) {
      console.error('Formulaire invalide', this.form.value);
      return;
    }
    this.etatPush = 'loading';

    if (this.typeSlug == 'ppc_ordoclic' && !this.connexionOrdoclic) {
      this.etatPush = false;
      return;
    }

    var prescriptionForm: PrescriptionForm = new PrescriptionForm(
      undefined,
      this.typeSlug,
      undefined,
      this.typeSlug == 'ppc_ordoclic'
        ? this.form.value.userId
        : this.form.value.medecin.id,
      undefined,
      this.form.value.userId,
      undefined,
      this.form.value.nir,
      this.form.value.codeDActivite,
      this.form.value.dateDePrescription.toUtcNativeDate(),
      this.form.value.nom,
      this.form.value.prenom,
      this.form.value.sexe,
      this.form.value.dateDeNaissance.toUtcNativeDate(),
      this.form.value.adresseLigne1,
      this.form.value.adresseLigne2,
      this.form.value.adresseCodePostal,
      this.form.value.adresseVille,
      this.form.value.telephone,
      this.typeSlug == 'ppc_ordoclic'
        ? false
        : (this.form.value.aSigner ?? false),
      this.typeSlug == 'ppc_ordoclic'
        ? true
        : (this.form.value.aSigner ?? false),
      null,
      undefined,
      this.prescription?.id,
      new PpcInformationsComplementaires(
        this.form.value.ppcTypeSlug,
        this.form.value.examenRealise,
        this.form.value.microEveils !== null &&
          this.form.value.microEveils !== undefined &&
          this.form.value.microEveils !== ''
          ? parseInt(this.form.value.microEveils)
          : undefined,
        this.form.value.iahGap,
        this.form.value.somnolenceDiurne,
        this.form.value.ronflementsSeveres,
        this.form.value.cephaleesMatinales,
        this.form.value.fatigueDiurne,
        this.form.value.nycturie,
        this.form.value.sensationEtouffement,
        this.form.value.somnolenceDiurneSevereRisqueAccidentel,
        this.form.value.comorbiditeCardioVasculaireGrave,
        this.form.value.comorbiditeRespiratoireGrave,
        this.form.value.oam12DerniersMoisNon,
        compteRenduBase64,
        this.form.value.reglage,
      ),
      (() => {
        switch (this.typeSlug) {
          case 'ppc_dap':
            return new PpcDapInformationsForm(
              this.form.value.caisseNom,
              this.form.value.assureNomComplet,
              this.form.value.assureNir,
              this.form.value.assureAdresse,
              this.form.value.maladie,
              this.form.value.ald,
              this.form.value.maladieDetail,
              this.form.value.traitementPrescritType,
              this.form.value.ppcAppareilType,
              this.form.value.ppcPression,
              this.form.value.ppcInterfaceType,
              this.form.value.ppcModeleMarque,
              this.form.value.oamExamenRealise,
              new Date(this.form.value.oamDate),
              this.form.value.oamType,
              this.form.value.oamModeleMarqueEtType,
              this.form.value.medecinConseilAccord,
              this.form.value.medecinConseilConvocation,
              this.form.value.medecinConseilRefusType,
              new Date(this.form.value.medecinConseilDate),
              this.form.value
                .initialeComorbiditeCardioVasculaireAssocieeGraveMotif,
              this.form.value.initialeComorbiditeRespiratoireAssocieeGraveMotif,
              this.form.value.ppcTypeSlug == 'dap_4mois'
                ? this.form.value.renouvellementIah
                : undefined,
              this.form.value.ppcTypeSlug == 'dap_4mois'
                ? this.form.value.renouvellementAmeliorationSymptomatologie
                : undefined,
              this.form.value.ppcTypeSlug == 'dap_4mois'
                ? this.form.value.renouvellementPatientObservant
                : undefined,
              this.form.value.ppcTypeSlug == 'dap_4mois'
                ? this.form.value.renouvellementRefusReleveObservance
                : undefined,
              this.form.value.ppcTypeSlug == 'dap_annuel'
                ? this.form.value.renouvellementIah
                : undefined,
              this.form.value.ppcTypeSlug == 'dap_annuel'
                ? this.form.value.renouvellementAmeliorationSymptomatologie
                : undefined,
              this.form.value.ppcTypeSlug == 'dap_annuel'
                ? this.form.value.renouvellementPatientObservant
                : undefined,
              this.form.value.ppcTypeSlug == 'dap_annuel'
                ? this.form.value.renouvellementRefusReleveObservance
                : undefined,
              this.form.value.oamIah,
              this.form.value.oamAmeliorationSymptomatologie,
              this.form.value.refusOuIntolerancePpc,
              this.form.value.refusOuIntoleranceOam,
            );
            break;
          case 'ppc_ordonnance':
            return new PpcOrdonnanceInformationsForm(
              this.form.value.assureNomComplet,
              undefined,
              this.form.value.caisseNom,
              this.form.value.ald,
              (() => {
                switch (this.form.value.ppcTypeSlug) {
                  case 'dap_4mois':
                    return '4 mois';
                  case 'dap_annuel':
                    return '12 mois';
                  default:
                    return '';
                }
              })(),
              this.form.value.ppcAppareilType,
              this.form.value.ppcPression,
              this.form.value.ppcInterfaceType,
              this.form.value.ppcModeleMarque,
            );
            break;
          case 'ppc_ordoclic':
            return new PpcOrdoclicInformationsForm(
              this.form.value.caisseNom,
              this.form.value.caisseNumero,
              this.form.value.ald,
              this.form.value.renouvellementIah,
              this.form.value.ppcAppareilType,
              this.form.value.ppcPression,
              this.form.value.ppcInterfaceType,
              this.form.value.ppcModeleMarque,
              this.form.value.renouvellementAmeliorationSymptomatologie,
              this.form.value.renouvellementPatientObservant,
              this.form.value.commentaire != ''
                ? this.form.value.commentaire
                : null,
            );
            break;

          default:
            return undefined;
        }
      })(),
    );

    if (this.typeSlug == 'ppc_ordoclic') {
      // si ordocic user == medecin
      if (this.authService.getUser()) {
        await this.launchOrdoclic(
          prescriptionForm.toOrdoclic(this.authService.getUser()!),
        ).then((response) => {
          prescriptionForm.pdf = response.prescription;
          if (
            prescriptionForm.informationsComplementaires instanceof
            PpcInformationsComplementaires
          ) {
            prescriptionForm.informationsComplementaires.recapitulatif =
              response.recapitulatif;
          }
        });
      }
    }

    this.prescriptionFormService.push(prescriptionForm).subscribe({
      next: (data) => {
        this.etatPush = true;
      },
      error: (error) => {
        this.etatPush = false;
      },
    });
  }

  async onSubmit() {
    if (this.prescriptionFormPrecedente && this.reedition) {
      this.prescriptionFormService
        .delete(this.prescriptionFormPrecedente)
        .subscribe({
          next: async (data) => {
            await this.post();
          },
          error: (error) => {
            console.log(error);
          },
        });
    } else {
      await this.post();
    }
  }

  ngOnDestroy(): void {
    this.ordoclicService.hideModal();
  }
}
